// src/features/etf-info/components/HoldingsTable.tsx
//
// Component Island (Island 3): the ETF's top underlying constituents ranked
// by portfolio weight (FMP already returns the basket weight-sorted). Each
// row is clickable and navigates to that constituent's stock deep-dive.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchEtfHoldings, type EtfHolding } from '../api';
import {
    formatCompactMoney,
    type CurrencyCode,
} from '../../../shared/utils/currency';

export const HoldingsTable = ({
    symbol,
    currency = 'USD',
}: {
    symbol: string;
    currency?: CurrencyCode;
}) => {
    const [holdings, setHoldings] = useState<EtfHolding[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);

        fetchEtfHoldings(symbol, 25)
            .then((res) => {
                if (!cancelled) setHoldings(res);
            })
            .catch((err) => {
                // eslint-disable-next-line no-console
                console.error(err);
                if (!cancelled) setHoldings([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [symbol]);

    return (
        <div className="surface-card h-full flex flex-col">
            <h2 className="section-title mb-1">Top Holdings</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                Underlying constituents ranked by portfolio weight
            </p>

            <div className="flex-1 overflow-auto min-h-[300px]">
                {isLoading ? (
                    <div className="h-full flex items-center justify-center text-slate-500 dark:text-slate-400 animate-pulse text-sm">
                        Loading holdings…
                    </div>
                ) : holdings.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-slate-500 dark:text-slate-400 text-sm">
                        No holdings data available.
                    </div>
                ) : (
                    <table className="w-full text-left text-sm whitespace-nowrap">
                        <thead className="text-xs uppercase bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 sticky top-0 z-10">
                            <tr>
                                <th className="p-3 font-semibold">Asset</th>
                                <th className="p-3 font-semibold text-right">Weight</th>
                                <th className="p-3 font-semibold text-right">Mkt Value</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/50">
                            {holdings.map((h, idx) => {
                                const weight = h.weight_percentage ?? 0;
                                return (
                                    <tr
                                        key={`${h.asset ?? 'row'}-${idx}`}
                                        onClick={() =>
                                            h.asset &&
                                            navigate(
                                                `/dashboard/stock-info-page?symbol=${h.asset}`,
                                            )
                                        }
                                        className="cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50"
                                    >
                                        <td className="p-3">
                                            <div className="font-bold text-slate-900 dark:text-white">
                                                {h.asset || '—'}
                                            </div>
                                            <div
                                                className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[160px]"
                                                title={h.name}
                                            >
                                                {h.name}
                                            </div>
                                        </td>
                                        <td className="p-3 text-right font-mono">
                                            <div className="text-slate-900 dark:text-slate-200 font-medium">
                                                {weight.toFixed(2)}%
                                            </div>
                                            <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                                                <div
                                                    className="h-full rounded-full bg-blue-500"
                                                    style={{
                                                        width: `${Math.min(100, weight)}%`,
                                                    }}
                                                />
                                            </div>
                                        </td>
                                        <td className="p-3 text-right font-mono font-medium text-slate-700 dark:text-slate-300">
                                            {formatCompactMoney(
                                                h.market_value,
                                                { explicitCurrency: currency },
                                                { fallback: 'N/A' },
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default HoldingsTable;
